import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Button, IconButton, TextField } from "@mui/material";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import React, { useEffect, useRef } from "react";
import SideNav from "component/layouts/sideNav";
import {
  ADD,
  DELETE,
  GO,
  LABOUR_MANAGEMENT,
  SUB_CONTRACTOR_FOOD_ADVANCE,
} from "resources/constant/strings/string";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import {
  siteCellRenderer,
  subContractorCellRenderer,
  supplierCellRenderer,
  userCellRenderer,
} from "component/cellRenderers/renderers";
import {
  LIGHT_GREY_BACKGROUND,
  PRUSSIAN_BLUE,
  SPANISH_GREEN,
  WHITE,
} from "resources/constant/colors/theme";

import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { APILABOURMGMT } from "apihandler/labourmgmt";
import { APILOGIN } from "apihandler/login";
import {
  setAlertConfirmDeletion,
  setAlertTitleText,
} from "component/modal/alertModal";
import {
  PATH_LOGIN,
  PATH_SUB_CONTRACTOR_FOOD_ADVANCE,
  PATH_SUB_CONTRACTOR_FOOD_ADVANCE_DETAIL,
} from "resources/constant/url/url";

import { useDispatch, useSelector } from "react-redux";
import {
  loadFoodAdvances,
  removeRowsByIds,
  setDates,
  setGridColumnState,
  setGridFilterModel,
  setGridPaginationPage,
  setGridSortModel,
  setHasInitializedOnce,
  setSelectedRowIds,
} from "store/Slice/foodAdvanceSlice";

export default function SubContractorFoodAdvance() {
  let { date } = useParams();
  const navigate = useNavigate();
  const gridRef = useRef();

  const dispatch = useDispatch();
  const {
    dateValue,
    toDateValue,
    rows,
    grid,
    selectedRowIds,
    hasInitializedOnce,
  } = useSelector((s) => s.foodAdvance);

  // auth check
  useEffect(() => {
    if (!APILOGIN.GetIsLoggedIn()) navigate(PATH_LOGIN);
  }, [navigate]);

  // set initial dates from URL & load
  useEffect(() => {
    const hasReduxDates = Boolean(dateValue && toDateValue);

    const from = date
      ? dayjs(date, "YYYY-MM-DD").format("YYYY-MM-DD")
      : hasReduxDates
      ? dateValue
      : dayjs().startOf("month").format("YYYY-MM-DD");

    const to = hasReduxDates
      ? toDateValue
      : dayjs(from, "YYYY-MM-DD").endOf("month").format("YYYY-MM-DD");

    if (date || !hasReduxDates) {
      dispatch(setDates({ from, to }));
      dispatch(loadFoodAdvances({ from, to }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const handleDelete = async () => {
    if (!selectedRowIds.length) return;

    const consent = await setAlertConfirmDeletion(
      "Confirmation needed",
      `Do you confirm on proceeding to delete ${selectedRowIds.length} record(s)?`
    );
    if (!consent.isConfirmed) return;

    try {
      const resp = await APILABOURMGMT.DeleteFoodAdvances(selectedRowIds);
      const data = await resp.json();
      if (data.statusCode === 204) {
        dispatch(removeRowsByIds(selectedRowIds));
        setAlertTitleText("Success", "The record was deleted successfully");
      } else {
        setAlertTitleText("Oops...", `Something went wrong...${data.message}`);
      }
    } catch (e) {
      console.log(e);
    }
  };

  // ===== grid state restore =====
  const restoreGridState = () => {
    const api = gridRef.current?.api;
    const columnApi = gridRef.current?.columnApi;
    if (!api || !columnApi) return;

    if (grid.filterModel) api.setFilterModel(grid.filterModel);
    if (grid.sortModel?.length) api.setSortModel(grid.sortModel);
    if (grid.columnState?.length)
      columnApi.applyColumnState({ state: grid.columnState, applyOrder: true });
    if (grid.quickFilterText) api.setQuickFilter(grid.quickFilterText);
    if (Number.isFinite(grid.paginationPage))
      api.paginationGoToPage(grid.paginationPage);

    if (!hasInitializedOnce) dispatch(setHasInitializedOnce(true));
  };

  const onGridReady = () => {
    if (rows && rows.length) restoreGridState();
  };

  const onFirstDataRendered = () => {
    restoreGridState();
  };

  const onFilterChanged = () => {
    const api = gridRef.current?.api;
    dispatch(setGridFilterModel(api.getFilterModel()));
  };

  const onSortChanged = () => {
    const api = gridRef.current?.api;
    dispatch(setGridSortModel(api.getSortModel() || []));
  };

  const onColumnMovedOrVisible = () => {
    const columnApi = gridRef.current?.columnApi;
    dispatch(setGridColumnState(columnApi.getColumnState() || []));
  };

  const onPaginationChanged = () => {
    const api = gridRef.current?.api;
    if (!api) return;
    dispatch(setGridPaginationPage(api.paginationGetCurrentPage() || 0));
  };

  const onSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    dispatch(setSelectedRowIds(selectedRows.map((r) => r.id)));
  };

  // ===== columns =====
  const columnDefs = [
    {
      wrapText: true,
      autoHeight: true,
      headerName: "Transaction No",
      field: "transactionNumber",
      checkboxSelection: false,
      cellStyle: { "border-right-color": "#e2e2e2" },
    },
    {
      wrapText: true,
      autoHeight: true,
      headerName: "Entry Date",
      field: "date",
      cellStyle: { "text-align": "center", "border-right-color": "#e2e2e2" },
    },
    {
      headerName: "Contractor Name",
      field: "contractorId",
      autoHeight: true,
      wrapText: true,
      valueGetter: (params) =>
        supplierCellRenderer({ value: params.data.contractorId }),
      cellStyle: { "border-right-color": "#e2e2e2" },
    },
    {
      headerName: "Site",
      field: "siteId",
      autoHeight: true,
      wrapText: true,
      valueGetter: (params) => siteCellRenderer({ value: params.data.siteId }),
      cellStyle: { "border-right-color": "#e2e2e2" },
    },
    {
      wrapText: true,
      autoHeight: true,
      headerName: "From Date",
      field: "fromDate",
      filter: false,
      floatingFilter: false,
      cellStyle: { "text-align": "center", "border-right-color": "#e2e2e2" },
    },
    {
      wrapText: true,
      autoHeight: true,
      headerName: "To Date",
      field: "toDate",
      filter: false,
      floatingFilter: false,
      cellStyle: { "text-align": "center", "border-right-color": "#e2e2e2" },
    },
    {
      wrapText: true,
      autoHeight: true,
      headerName: "Payable",
      field: "payable",
      filter: false,
      floatingFilter: false,
      cellStyle: { "text-align": "center", "border-right-color": "#e2e2e2" },
    },
    {
      flex: 1.5,
      headerName: "Bill No",
      field: "billNumber",
      autoHeight: true,
      wrapText: true,
      valueGetter: (params) =>
        subContractorCellRenderer({ value: params.data.billNumber }),
      cellStyle: { "border-right-color": "#e2e2e2" },
    },
    {
      headerName: "Modified By",
      field: "modifiedBy",
      valueGetter: (params) =>
        userCellRenderer({ value: params.data.modifiedBy }),
      cellStyle: { "border-right-color": "#e2e2e2" },
      autoHeight: true,
      wrapText: true,
    },
    {
      flex: 1,
      headerName: "Details",
      field: "id",
      sortable: false,
      filter: false,
      floatingFilter: false,
      cellRenderer: (params) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {params.data?.billNumber === 0 && (
            <IconButton
              aria-label="edit"
              onClick={() => {
                navigate(
                  PATH_SUB_CONTRACTOR_FOOD_ADVANCE_DETAIL + params.data.id,
                  {
                    state: {
                      date: dayjs(dateValue).format("YYYY-MM-DD"),
                      isEdit: true,
                    },
                  }
                );
              }}
            >
              <EditIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
            </IconButton>
          )}
          <IconButton
            aria-label="view"
            onClick={() => {
              navigate(
                PATH_SUB_CONTRACTOR_FOOD_ADVANCE_DETAIL + params.data.id,
                {
                  state: {
                    date: dayjs(dateValue).format("YYYY-MM-DD"),
                    isEdit: false,
                  },
                }
              );
            }}
          >
            <VisibilityIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
          </IconButton>
          <IconButton
            aria-label="print"
            onClick={() => {
              let filename = "";
              APILABOURMGMT.FoodAdvancePDF(params.data.id)
                .then((res) => {
                  const contentType = res.headers.get("content-type");
                  if (contentType.startsWith("application/json")) {
                    return Promise.reject(res.json());
                  } else {
                    const header = res.headers.get("Content-Disposition");
                    if (header !== null) {
                      const parts = header.split(";");
                      filename = parts[1].split("=")[1].replaceAll('"', "");
                    } else {
                      filename = "FoodAdvance.html";
                    }
                    return res.blob();
                  }
                })
                .then((blob) => {
                  if (blob !== null) {
                    blob.text().then((x) => {
                      let html = x.replace(
                        "<script>",
                        '<script type="text/javascript">'
                      );
                      let b = new Blob([html], { type: "text/html" });
                      let url = window.URL.createObjectURL(b, {
                        oneTimeOnly: true,
                      });
                      let win = window.open(url, "_blank", "");
                      setTimeout(() => {
                        win?.print();
                      }, 2000);
                    });
                  }
                })
                .catch((m) => {
                  setAlertTitleText(
                    "Oops...",
                    `Something went wrong...${m.message}`
                  );
                });
            }}
          >
            <PrintIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
          </IconButton>
        </div>
      ),
    },
  ];

  const defaultColDef = {
    resizable: true,
    sortable: true,
    editable: false,
    flex: 1,
    filter: "agTextColumnFilter",
    filterParams: {
      caseSensitive: false,
      defaultOption: "startsWith",
    },
    floatingFilter: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  };

  return (
    <SideNav
      currentComponent={LABOUR_MANAGEMENT}
      subComponent={SUB_CONTRACTOR_FOOD_ADVANCE}
      subComponentPath={PATH_SUB_CONTRACTOR_FOOD_ADVANCE}
      detailComponent=""
    >
      {/* actions */}
      <div
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          marginBottom: 10,
          marginRight: 10,
        }}
      >
        <Button
          style={{
            backgroundColor: SPANISH_GREEN,
            textTransform: "capitalize",
            fontSize: 14,
            color: WHITE,
            minWidth: "180px",
            minHeight: "45px",
          }}
          onClick={() => {
            navigate(PATH_SUB_CONTRACTOR_FOOD_ADVANCE_DETAIL + 0, {
              state: {
                date: dayjs(dateValue).format("YYYY-MM-DD"),
                isEdit: false,
              },
            });
          }}
        >
          {ADD}
        </Button>

        <Button
          onClick={handleDelete}
          variant="outlined"
          sx={{ borderColor: SPANISH_GREEN }}
          style={{
            textTransform: "capitalize",
            fontSize: 14,
            color: SPANISH_GREEN,
            minWidth: "180px",
            minHeight: "45px",
            marginRight: 10,
          }}
        >
          {DELETE}
        </Button>
      </div>

      {/* filter row */}
      <div
        style={{
          backgroundColor: LIGHT_GREY_BACKGROUND,
          borderRadius: "5px",
          minWidth: "90%",
          minHeight: "50px",
          padding: 20,
          marginBottom: 10,
          borderStyle: "solid",
          border: "1x",
          borderColor: "#eaeaea",
          display: "flex",
        }}
      >
        <div style={{ display: "flex" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="dateId"
              label="From Date"
              value={dayjs(dateValue, "YYYY-MM-DD")}
              onChange={(newValue) => {
                if (!newValue) return;
                dispatch(
                  setDates({
                    from: newValue.format("YYYY-MM-DD"),
                    to: toDateValue,
                  })
                );
              }}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="toDateId"
              label="To Date"
              value={dayjs(toDateValue, "YYYY-MM-DD")}
              onChange={(newValue) => {
                if (!newValue) return;
                dispatch(
                  setDates({
                    from: dateValue,
                    to: newValue.format("YYYY-MM-DD"),
                  })
                );
              }}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => (
                <TextField {...params} style={{ marginLeft: 10 }} />
              )}
            />
          </LocalizationProvider>
        </div>
        <div
          align="right"
          style={{
            width: "100%",
            right: 0,
          }}
        >
          <Button
            style={{
              backgroundColor: SPANISH_GREEN,
              textTransform: "capitalize",
              fontSize: 14,
              color: WHITE,
              minWidth: "129px",
              minHeight: "45px",
              marginTop: 5,
            }}
            onClick={() => {
              dispatch(loadFoodAdvances({ from: dateValue, to: toDateValue }));
            }}
          >
            {GO}
          </Button>
        </div>
      </div>

      {/* grid */}
      <div className="App">
        <div className="ag-theme-alpine" style={{ height: "570px" }}>
          <AgGridReact
            ref={gridRef}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowData={rows}
            pagination
            paginationAutoPageSize
            rowSelection="multiple"
            enableRangeSelection={true}
            suppressCopyRowsToClipboard={true}
            getRowId={(params) => params.data.id}
            onGridReady={onGridReady}
            onFirstDataRendered={onFirstDataRendered}
            onFilterChanged={onFilterChanged}
            onSortChanged={onSortChanged}
            onColumnMoved={onColumnMovedOrVisible}
            onColumnVisible={onColumnMovedOrVisible}
            onPaginationChanged={onPaginationChanged}
            onSelectionChanged={onSelectionChanged}
            suppressBrowserResizeObserver={true}
          />
        </div>
      </div>
    </SideNav>
  );
}
